import { useCallback, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { GripVertical, X } from "lucide-react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import type { Location } from "@/types/Location";
import { getLocationLabel } from "@/lib/utils";

const ITEM_TYPE = "LOCATION";

interface DraggableLocationItemProps {
  location: Location;
  index: number;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  onRemove: (locationId: string) => void;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const DraggableLocationItem = ({ location, index, moveItem, onRemove }: DraggableLocationItemProps) => {
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { index, id: location.id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop<DragItem>({
    accept: ITEM_TYPE,
    hover: (item: DragItem) => {
      if (!item) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  return (
    <div
      ref={(node) => {
        drag(drop(node));
      }}
      className={`flex items-center gap-3 p-2 bg-white border rounded-md cursor-move hover:bg-gray-50 transition-colors ${
        isDragging ? "opacity-50" : "opacity-100"
      }`}
    >
      <div className="flex items-center gap-3 flex-1">
        <GripVertical className="h-5 w-5 text-gray-400" />
        <Avatar className="h-8 w-8">
          <AvatarImage src={location.locationImageUrl} alt={getLocationLabel(location)} />
          <AvatarFallback>{location.name.substring(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <span className="text-sm font-medium">{getLocationLabel(location)}</span>
      </div>
      <Button variant="ghost" size="sm" onClick={() => onRemove(location.id)} className="h-8 w-8 p-0">
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

interface PreferredLocationsSelectorProps {
  selectedLocations: Location[];
  availableLocations: Location[];
  onChange: (locationIds: string[]) => void;
  maxItems?: number;
}

const PreferredLocationsSelectorContent = ({
  selectedLocations,
  availableLocations,
  onChange,
  maxItems = 5,
}: PreferredLocationsSelectorProps) => {
  const [locations, setLocations] = useState<Location[]>(selectedLocations);

  const moveItem = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const draggedItem = locations[dragIndex];
      const newLocations = [...locations];
      newLocations.splice(dragIndex, 1);
      newLocations.splice(hoverIndex, 0, draggedItem);
      setLocations(newLocations);
      onChange(newLocations.map((loc) => loc.id));
    },
    [locations, onChange],
  );

  const handleRemove = useCallback(
    (locationId: string) => {
      const newLocations = locations.filter((loc) => loc.id !== locationId);
      setLocations(newLocations);
      onChange(newLocations.map((loc) => loc.id));
    },
    [locations, onChange],
  );

  const handleAdd = useCallback(
    (location: Location) => {
      if (locations.length >= maxItems) {
        return;
      }
      const newLocations = [...locations, location];
      setLocations(newLocations);
      onChange(newLocations.map((loc) => loc.id));
    },
    [locations, maxItems, onChange],
  );

  const unselectedLocations = availableLocations.filter((loc) => !locations.some((selected) => selected.id === loc.id));

  // Sort unselected locations alphabetically by label for the "Available locations" list
  const sortedUnselected = [...unselectedLocations].sort((a, b) =>
    getLocationLabel(a).localeCompare(getLocationLabel(b), undefined, { sensitivity: "base" }),
  );

  return (
    <div className="space-y-4">
      {/* Selected locations with drag and drop */}
      {locations.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            Drag to reorder ({locations.length}/{maxItems} selected)
          </p>
          <div className="space-y-2">
            {locations.map((location, index) => (
              <DraggableLocationItem
                key={location.id}
                location={location}
                index={index}
                moveItem={moveItem}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </div>
      )}

      {/* Available locations to add */}
      {unselectedLocations.length > 0 && locations.length < maxItems && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">Available locations:</p>
          <div className="space-y-2">
            {sortedUnselected.map((location) => (
              <div
                key={location.id}
                className="flex items-center gap-3 p-2 bg-gray-50 border border-dashed rounded-md hover:bg-gray-100 transition-colors"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={location.locationImageUrl} alt={getLocationLabel(location)} />
                  <AvatarFallback>{location.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium flex-1">{getLocationLabel(location)}</span>
                <Button variant="outline" size="sm" onClick={() => handleAdd(location)} className="h-8">
                  Add
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {locations.length === 0 && (
        <p className="text-sm text-gray-500 italic">No preferred locations selected. Add some from the list above.</p>
      )}

      {locations.length >= maxItems && unselectedLocations.length > 0 && (
        <p className="text-sm text-amber-600">
          Maximum of {maxItems} locations reached. Remove a location to add another.
        </p>
      )}
    </div>
  );
};

export const PreferredLocationsSelector = (props: PreferredLocationsSelectorProps) => {
  return (
    <DndProvider backend={HTML5Backend}>
      <PreferredLocationsSelectorContent {...props} />
    </DndProvider>
  );
};
